import type { Dispatch, SetStateAction } from "react";

import { BanIcon, CircleCheckIcon, LoaderCircleIcon } from "lucide-react";
import { UploadIcon } from "lucide-react";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { pluralise } from "simplydo/core/helpers";
import { styled } from "simplydo/react/styled";
import { Card, CardContent } from "simplydo/react/ui/card";
import { H3, H5 } from "simplydo/react/ui/header";
import { Icon, IconData } from "simplydo/react/ui/icon";
import { Progress } from "simplydo/react/ui/progress";

import { type DragFile, type UploadState, verifyType } from "./uploadUtils";

type StyledDropZoneProps = {
  children: React.ReactNode;
  style?: React.CSSProperties;
  onClick?: () => void;
};

const StyledDropZone = (props: StyledDropZoneProps) => {
  const { children, ...rest } = props;
  return (
    <Card elevation="small" fluid placeholder {...rest}>
      <CardContent>{children}</CardContent>
    </Card>
  );
};

const DropZoneStyling = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;

  min-height: 12rem;

  > div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  > h5,
  > div > h5,
  > div > h3 {
    margin: 0 0 3px 0;
    display: block;
    color: #808080;
    > u {
      color: ${(props) =>
        props.theme ? props.theme.primaryColour : "#0078d4"};
      cursor: pointer;
    }
  }
  > i.icon,
  > svg.icon {
    font-size: 3em !important;
    color: #808080;
  }
`;

export type DropZoneConfigProps = {
  title?: string;
  subtitle?: string;
  defaultIcon?: IconData;
  onClick?: () => void;
  disabled?: boolean;
};

export type DropZoneProps = {
  uploadState: UploadState;
  setUploadState: Dispatch<SetStateAction<UploadState>>;
  accept?: string;
  draggedOverFiles?: Array<DragFile>;
  allowMultiple?: boolean;
  style?: React.CSSProperties;
} & DropZoneConfigProps;

export const DropZone = ({
  disabled,
  accept,
  uploadState,
  draggedOverFiles,
  allowMultiple,
  style,
  onClick,
  title,
  subtitle,
  defaultIcon = UploadIcon,
}: DropZoneProps) => {
  if (uploadState.isUploading) {
    return (
      <StyledDropZone style={style}>
        <DropZoneStyling>
          <Icon size="large" icon={LoaderCircleIcon} />
          <Progress
            percent={uploadState.uploadProgress}
            indicating
            progress
            style={{ width: "100%" }}
          >
            Uploading...
          </Progress>
        </DropZoneStyling>
      </StyledDropZone>
    );
  }

  if (disabled) {
    return (
      <StyledDropZone style={style}>
        <DropZoneStyling>
          <Icon size="large" icon={BanIcon} />
          <H5>Cannot upload more files</H5>
        </DropZoneStyling>
      </StyledDropZone>
    );
  }

  if ((draggedOverFiles?.length ?? 0) > 0) {
    if (!allowMultiple && (draggedOverFiles?.length ?? 0) > 1) {
      return (
        <StyledDropZone style={style}>
          <DropZoneStyling>
            <Icon size="large" icon={BanIcon} />
            <H5>Cannot upload more than one file</H5>
          </DropZoneStyling>
        </StyledDropZone>
      );
    }
    if (accept && draggedOverFiles) {
      for (let i = 0; i < draggedOverFiles.length; i++) {
        const draggedFile = draggedOverFiles[i];
        if (!draggedFile) continue;
        const typeCheck = verifyType(accept, draggedFile.type);
        if (!typeCheck.allowed) {
          return (
            <StyledDropZone style={style}>
              <DropZoneStyling>
                <Icon size="large" icon={BanIcon} />
                <H5>{typeCheck.error}</H5>
              </DropZoneStyling>
            </StyledDropZone>
          );
        }
      }
    }
    return (
      <StyledDropZone style={style}>
        <DropZoneStyling>
          <Icon size="large" icon={CircleCheckIcon} />
          <H5>
            Drop the {pluralise(draggedOverFiles?.length ?? 0, "file", "files")}{" "}
            to start the upload
          </H5>
        </DropZoneStyling>
      </StyledDropZone>
    );
  }

  return (
    <StyledDropZone style={style} onClick={onClick}>
      <DropZoneStyling>
        <Icon size="large" icon={defaultIcon} />
        <div>
          {title ? <H3>{title}</H3> : null}
          {subtitle ? <H5>{subtitle}</H5> : null}
          <H5 style={{ marginTop: 6 }}>
            Drop a file here to upload, or{" "}
            <u
              role="button"
              tabIndex={0}
              onClick={onClick}
              onKeyDown={handleClickForKeyboard(onClick)}
            >
              click to browse
            </u>
            .{allowMultiple ? " You can upload multiple files at once." : ""}
          </H5>
        </div>
      </DropZoneStyling>
    </StyledDropZone>
  );
};
